import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import CgButton from './CgButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';

/**
 * A button that opens a dropdown menu when clicked.
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Button label
 * @param {string} props.variant - Button variant (primary, secondary, etc.)
 * @param {string} props.icon - Button icon name
 * @param {boolean} props.outlined - Whether button should be outlined
 * @param {string} props.size - Button size (sm, md, lg)
 * @param {Array} props.options - Array of dropdown options, each with { label, icon, onClick }
 * @returns {JSX.Element} Rendered component
 */
const CgButtonDropdown = ({
  label,
  variant = 'primary',
  icon,
  outlined = false,
  size = 'sm',
  options = [],
  dropdownWidth = 'w-72',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className="relative inline-block"
      ref={dropdownRef}
    >
      <div className="flex items-center">
        <CgButton
          variant={variant}
          outlined={outlined}
          size={size}
          icon={icon}
          label={label}
          onClick={() => setIsOpen(!isOpen)}
        />
      </div>
      {isOpen && (
        <div
          className={`absolute right-0 mt-2 ${dropdownWidth} rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50`}
        >
          <div className="py-1">
            {options.map((option, index) => (
              <button
                key={index}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                onClick={() => {
                  option.onClick();
                  setIsOpen(false);
                }}
              >
                {option.icon && <span className="mr-2">{option.icon}</span>}
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

CgButtonDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  variant: PropTypes.string,
  icon: PropTypes.string,
  outlined: PropTypes.bool,
  size: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  dropdownWidth: PropTypes.string,
};

export default CgButtonDropdown;
